import * as Sentry from '@sentry/vue';
import { createApp } from 'vue';
import type { App, Component } from 'vue';

function resolveDsn(): string | undefined {
  return import.meta.env.VITE_SENTRY_DSN || document.querySelector<HTMLMetaElement>('meta[name="sentry-dsn"]')?.content;
}

export function ensureSentryInit(): void {
  const dsn = resolveDsn();
  if (!dsn || Sentry.getClient()) return;
  Sentry.init({
    dsn,
    environment: import.meta.env.MODE,
    integrations: (defaults) => [...defaults.filter((i) => i.name !== 'Vue'), Sentry.browserTracingIntegration()],
    tracesSampleRate: import.meta.env.MODE === 'production' ? 0.2 : 0,
  });
}

export function createTrackedApp(rootComponent: Component, rootProps?: Record<string, unknown>): App {
  const app = createApp(rootComponent, rootProps);
  const dsn = resolveDsn();
  if (!dsn) return app;

  if (!Sentry.getClient()) {
    Sentry.init({
      app,
      dsn,
      environment: import.meta.env.MODE,
      integrations: [Sentry.browserTracingIntegration(), Sentry.replayIntegration()],
      tracesSampleRate: import.meta.env.MODE === 'production' ? 0.2 : 0,
      replaysSessionSampleRate: 0,
      replaysOnErrorSampleRate: import.meta.env.MODE === 'production' ? 1.0 : 0,
    });
  } else {
    // Already initialized (multiple Vue apps on same page) — attach error handler only.
    Sentry.attachErrorHandler(app, {
      attachProps: true,
      tracingOptions: { trackComponents: true, timeout: 2000 },
    });
  }

  return app;
}

export { Sentry };
